import React, { useState } from 'react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isFirstPartyRedirect } from '../utils/firstPartyRedirect';
import { safeInternalNext } from './LoginPage';
import AgeGatePrompt, {
  AgeGateRefusal,
  ageAttestTokenOf,
  isAgeRefusal,
  markAgeGateBlocked,
  postAgeAttestation,
  storeAttestedSession,
} from '../components/AgeGate';

const AGE_ATTEST_FAILED = 'We could not verify your date of birth. Please try again.';
const AGE_SIGN_IN_AGAIN = 'Please sign in again to continue.';

export default function TOTPVerifyPage() {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { loginWithTOTP, loadUser } = useAuth();
  // 999.18844 #5 — an account that never attested its age gets 403 + a
  // short-lived age_attest token from /login-totp instead of a session;
  // agePrompt holds that token ({ token }) while the date of birth is asked.
  const [agePrompt, setAgePrompt] = useState(null);
  const [ageRefused, setAgeRefused] = useState(false);
  const [ageError, setAgeError] = useState('');
  const [ageSubmitting, setAgeSubmitting] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // 999.19315 — set by LoginPage via router state; never from the URL.
  const location = useLocation();
  const totpPendingToken = location.state?.totpPendingToken;
  const redirectUrl = searchParams.get('redirect');
  const nextParam = searchParams.get('next');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await loginWithTOTP(totpPendingToken, code);
      continueAfterLogin();
    } catch (err) {
      const attestToken = ageAttestTokenOf(err);
      if (attestToken) {
        setAgePrompt({ token: attestToken });
        return;
      }
      setError(err.data?.message || err.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  function continueAfterLogin() {
    // 999.2085: only follow a first-party redirect target — an unvalidated
    // ?redirect= here is an open redirect on the 2FA SUCCESS path. Off-allowlist
    // targets fall back to the account page.
    if (redirectUrl && isFirstPartyRedirect(redirectUrl)) {
      window.location.href = redirectUrl;
    } else {
      // 999.17990 round 2: no first-party `redirect` — fall back to a
      // validated in-app `next` (e.g. /consent?oauth_state=X carried through
      // the 2FA hop by LoginPage) before defaulting to /account. Re-validated
      // here with the SAME safeInternalNext check LoginPage used before
      // putting it on the URL — never trust a query param on say-so alone.
      navigate(safeInternalNext(nextParam) || '/account');
    }
  }

  async function submitAgeAttestation(birthDate) {
    setAgeError('');
    setAgeSubmitting(true);
    try {
      const data = await postAgeAttestation(agePrompt.token, birthDate);
      storeAttestedSession(data);
      await loadUser();
      setAgePrompt(null);
      continueAfterLogin();
    } catch (err) {
      if (isAgeRefusal(err)) {
        markAgeGateBlocked();
        setAgePrompt(null);
        setAgeRefused(true);
        return;
      }
      if (err && err.status === 401) {
        // spent or expired attest token: only a fresh login carries a new one
        setAgePrompt(null);
        setError(AGE_SIGN_IN_AGAIN);
        return;
      }
      setAgeError(AGE_ATTEST_FAILED);
    } finally {
      setAgeSubmitting(false);
    }
  }

  // 999.19315 — no pending token (reload / direct visit): the password step
  // must run again; keep redirect/app/next on the way back.
  if (!totpPendingToken) return <Navigate to={`/login${location.search}`} replace />;

  if (ageRefused || agePrompt) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <h1>{ageRefused ? 'Two-Factor Authentication' : 'One more step'}</h1>
          {ageRefused ? (
            <AgeGateRefusal />
          ) : (
            <AgeGatePrompt context="existing" onSubmit={submitAgeAttestation} submitting={ageSubmitting} error={ageError} />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <h1>Two-Factor Authentication</h1>
        <p className="subtitle">Enter the code from your authenticator app</p>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Verification Code</label>
            <input
              type="text" value={code} onChange={e => setCode(e.target.value)}
              placeholder="Enter 6-digit code or backup code"
              required autoFocus autoComplete="one-time-code"
              style={{ textAlign: 'center', fontSize: '1.25rem', letterSpacing: '0.15em' }}
            />
          </div>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Verifying...' : 'Verify'}
          </button>
        </form>
      </div>
    </div>
  );
}
